import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import useScanner from '@afosto/utils-react/input/useScanner';
import { useSnackbar } from 'notistack';
import { useTheme } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import InputAdornment from '@material-ui/core/InputAdornment';
import ParcelDialog from './ParcelDialog';
import Page from '@afosto/app-components-react/Page';
import SearchBar from '@afosto/app-components-react/SearchBar';
import { index as searchApi } from '@afosto/content/api/Index';
import { parcels as parcelsApi } from '@afosto/fulfilment/api/Parcels';
import translations from './translations';

const ParkParcels = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'lcs:handling:read',
    'lcs:shipments:read',
    'lcs:locations:read',
    'rel:contacts:read',
  ]);

  const intl = useIntl();
  const [inputFocus, setInputFocus] = useState(false);
  const [isSearchingParcel, setIsSearchingParcel] = useState(false);
  const [parcel, setParcel] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  const handleSearch = async value => {
    try {
      setIsSearchingParcel(true);
      setSearchValue(value);

      const response = await searchApi.search({
        codes: ['lcs'],
        models: ['parcels'],
        namespaces: ['default'],
        q: [value],
        is_fuzzy: false,
      });
      const { data } = response;
      const [firstHit] = data?.hits ?? [];

      if (firstHit) {
        const parcelResponse = await parcelsApi.getParcel(firstHit.reference);
        const { data: parcelData } = parcelResponse;

        if (!parcelData?.is_in_transit && !parcelData?.is_finished) {
          setParcel(parcelData);
        } else {
          setSearchValue('');
          enqueueSnackbar(intl.formatMessage(translations.feedback.parcelAlreadySent), {
            variant: 'info',
          });
        }
      } else {
        setSearchValue('');
        enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotFound), {
          variant: 'info',
        });
      }
    } catch (err) {
      setParcel(null);
    } finally {
      setIsSearchingParcel(false);
    }
  };

  useScanner(handleSearch, { disabled: inputFocus || !!parcel });

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Page title={intl.formatMessage(translations.title)} fullWidth>
      <SearchBar
        position="static"
        value={searchValue}
        searchInputProps={{
          endAdornment: isSearchingParcel && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ),
          placeholder: intl.formatMessage(translations.search.placeholder),
          onBlur: () => setInputFocus(false),
          onFocus: () => setInputFocus(true),
        }}
        onSearch={handleSearch}
      />
      <ParcelDialog
        isOpen={!!parcel}
        onClose={() => {
          setSearchValue('');
          setParcel(null);
        }}
        onSave={() => {
          setSearchValue('');
          setParcel(null);
        }}
        parcel={parcel}
        fullscreen={isMobile}
        disableBackdropClick
      />
    </Page>
  );
};

export default ParkParcels;
